<template>
    <div
        ref="filterPanel"
        :class="{
            'fixed top-0 left-0 w-full h-screen bg-black overflow-y-auto transition-transform translate-x-full':
                $device.isMobileOrTablet,
            'translate-x-0': $store.state.jobs.isMobileFilterPanelOpen,
        }"
    >
        <div :class="{ 'container mt-12 pb-32': $device.isMobileOrTablet }">
            <div class="flex items-center justify-between lg:hidden mb-5">
                <span class="title title-lg">{{ $t('jobs.search.section_title') }}</span>

                <button
                    v-if="$device.isMobileOrTablet"
                    class="inline-flex items-center p-2"
                    @click="closeMobileFilterPanel"
                >
                    <i class="pi pi-times"></i>
                </button>
            </div>

            <div class="grid grid-cols-12 gap-5 pb-5">
                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.country') }}</span>
                    <MultiSelectFilter
                        :value="filters.country"
                        :options="options.country"
                        :set="(val) => setFilter('country', val)"
                        option-label="name"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>

                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.city') }}</span>
                    <MultiSelectFilter
                        :value="filters.region"
                        :options="options.region"
                        :set="(val) => setFilter('region', val)"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>

                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.employment') }}</span>
                    <MultiSelectFilter
                        :value="filters.employment_type"
                        :options="options.employment_type"
                        :set="(val) => setFilter('employment_type', val)"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>

                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.industry') }}</span>
                    <MultiSelectFilter
                        :value="filters.sector"
                        :options="options.sector"
                        :set="(val) => setFilter('sector', val)"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>

                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.position') }}</span>
                    <MultiSelectFilter
                        :value="filters.position"
                        :options="options.position"
                        :set="(val) => setFilter('position', val)"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>

                <div class="p-field col-span-6 lg:col-span-4" :class="{ 'xl:col-span-2': !$device.isMobileOrTablet }">
                    <span class="ml-5 mb-1 text-xs sm:text-sm">{{ $t('jobs.filters.languages') }}</span>
                    <MultiSelectFilter
                        :value="filters.language"
                        :options="options.language"
                        :set="(val) => setFilter('language', val)"
                        light
                        has-wide-dropdown-panel
                        is-panel-always-below
                    />
                </div>
            </div>

            <div v-if="filterChips.length" class="-mx-2">
                <span
                    v-for="filter in filterChips"
                    :key="`${filter.key}-${filter.id}`"
                    class="
                        relative
                        inline-flex
                        items-center
                        rounded-full
                        py-2
                        pl-4
                        pr-8
                        text-xs
                        sm:text-sm
                        my-2
                        mx-1
                        transition
                        border border-white/30
                    "
                >
                    <span class="mr-2 cursor-default">{{ filter.label }}</span>
                    <button
                        class="absolute top-2 right-4"
                        @mouseenter="handleDeleteChipButtonMouseEnter"
                        @mouseleave="handleDeleteChipButtonMouseLeave"
                        @click="removeFilter(filter)"
                    >
                        <i class="pi pi-times text-sm"></i>
                    </button>
                </span>

                <button
                    class="
                        inline-flex
                        items-center
                        rounded-full
                        py-2
                        pl-4
                        pr-4
                        text-xs
                        sm:text-sm
                        transition
                        desktop:hover:bg-white desktop:hover:text-black
                        my-2
                        mx-1
                    "
                    @click="resetFilters"
                >
                    <i class="pi pi-refresh" style="transform: rotateY(180deg)"></i>
                    <span class="ml-2 font-bold">Törlöm a beállításokat</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import MultiSelectFilter from '@/components/UI/Inputs/MultiSelectFilter';

export default {
    name: 'JobFilters',
    components: {
        MultiSelectFilter,
    },
    props: {
        filters: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        setFilter: {
            type: Function,
            required: true,
        },
        resetFilters: {
            type: Function,
            required: true,
        },
        closeMobileFilterPanel: {
            type: Function,
            required: false,
            default: () => {},
        },
    },
    computed: {
        filterChips() {
            return Object.keys(this.filters).reduce((acc, curr) => {
                if (this.filters[curr]) {
                    const sortedFilters = this.$clone(this.filters[curr]).sort((a, b) => b.id - a.id);
                    sortedFilters.forEach((filter) => {
                        acc.push({
                            key: curr,
                            id: filter.id,
                            label: filter.title ? filter.title : filter.name ? filter.name : '',
                            ...filter,
                        });
                    });
                }
                return acc;
            }, []);
        },
    },
    methods: {
        handleDeleteChipButtonMouseEnter(e) {
            e.target.parentElement.classList.add('border-white');
        },
        handleDeleteChipButtonMouseLeave(e) {
            e.target.parentElement.classList.remove('border-white');
        },
        removeFilter(filter) {
            const updatedFilters = this.filters[filter.key].filter((x) => {
                return x.id !== filter.id;
            });

            this.setFilter(filter.key, updatedFilters);
        },
    },
};
</script>

<style scoped></style>
